import { useState, useRef } from 'react'
import { AlertTriangle, CheckCircle2, Clock, Info, Loader2, MinusCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { apiService } from '@/services/apiService'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { usePools } from '@/hooks/use-pools'

type Step = 'form' | 'checking' | 'confirm' | 'executing' | 'done'
type StepStatus = 'pending' | 'running' | 'done' | 'error' | 'skipped'
type OtherPoolBehavior = 'add' | 'remove' | 'skip'

interface CheckInfo {
  otherPools: string[]
  alreadyInTargetPool: boolean
  touEnabled: boolean
  checkError?: string
}

interface ExecStep {
  label: string
  status: StepStatus
  error?: string
}

interface Props {
  poolID: string
  poolPurpose?: string | null
  touFallback?: boolean
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

function StatusIcon({ status, className }: { status: StepStatus; className?: string }) {
  const cls = cn('h-4 w-4 flex-shrink-0', className)
  switch (status) {
    case 'running': return <Loader2 className={cn(cls, 'animate-spin text-blue-500')} />
    case 'done': return <CheckCircle2 className={cn(cls, 'text-green-500')} />
    case 'error': return <XCircle className={cn(cls, 'text-destructive')} />
    case 'skipped': return <MinusCircle className={cn(cls, 'text-muted-foreground')} />
    default: return <Clock className={cn(cls, 'text-muted-foreground')} />
  }
}

export default function AddSystemDialog({
  poolID,
  poolPurpose,
  touFallback = false,
  open,
  onClose,
  onSuccess,
}: Props) {
  const [step, setStep] = useState<Step>('form')
  const [systemID, setSystemID] = useState('')
  const [activeFrom, setActiveFrom] = useState('')
  const [otherPoolBehavior, setOtherPoolBehavior] = useState<OtherPoolBehavior>('skip')
  const [disableTou, setDisableTou] = useState(false)
  const [checkInfo, setCheckInfo] = useState<CheckInfo | null>(null)
  const [execSteps, setExecSteps] = useState<ExecStep[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const cancelledRef = useRef(false)
  const { toast } = useToast()
  const qc = useQueryClient()
  const { data: allPools = [] } = usePools()
  const isControlPoolId = (id: string) => !!allPools.find((p) => p.poolID === id)?.controlConfig?.purpose
  const isControlPool = !!poolPurpose
  const isExecuting = step === 'executing'
  const needsPhaseLimitsNotice =
    poolPurpose === 'imbalance' || poolPurpose === 'tou_stacking_poc'

  const reset = () => {
    cancelledRef.current = true
    setStep('form')
    setSystemID('')
    setActiveFrom('')
    setOtherPoolBehavior('skip')
    setDisableTou(false)
    setCheckInfo(null)
    setExecSteps([])
  }

  const handleClose = () => {
    if (isExecuting) return
    if (step === 'done') onSuccess?.()
    reset()
    onClose()
  }

  // ── Step 1 → 2: check ──────────────────────────────────────────────────────

  const startChecking = async () => {
    if (!systemID.trim()) {
      toast({ title: 'Validation Error', description: 'System ID is required', variant: 'destructive' })
      return
    }
    cancelledRef.current = false
    setStep('checking')

    const trimmedID = systemID.trim()

    try {
      const poolsResp = await apiService.systems.getPoolsBySystem(trimmedID)
      if (cancelledRef.current) return
      const allPools: string[] = poolsResp.success ? (poolsResp.data ?? []) : []
      const alreadyInTargetPool = allPools.includes(poolID)
      const otherPools = allPools.filter((p) => p !== poolID)

      let touEnabled = false
      if (!alreadyInTargetPool && (isControlPool ? disableTou : true) && !(otherPoolBehavior === 'skip' && otherPools.length > 0)) {
        const touResp = await apiService.tou.getSettings(trimmedID)
        if (cancelledRef.current) return
        if (!touResp.success && touResp.errorCode === 'NOT_FOUND') {
          const existsResp = await apiService.systems.get(trimmedID)
          if (!existsResp.success && existsResp.errorCode === 'NOT_FOUND') {
            setCheckInfo({ otherPools: [], alreadyInTargetPool: false, touEnabled: false, checkError: 'System does not exist' })
            setStep('confirm')
            return
          }
        } else if (touResp.success && touResp.data != null) {
          touEnabled = (touResp.data as any).enabled ?? false
        }
      }

      setCheckInfo({ otherPools, alreadyInTargetPool, touEnabled })
    } catch {
      setCheckInfo({ otherPools: [], alreadyInTargetPool: false, touEnabled: false, checkError: 'Check failed' })
    }
    setStep('confirm')
  }

  // ── Step 3 → 4: execute ────────────────────────────────────────────────────

  const buildSteps = (info: CheckInfo): ExecStep[] => {
    const steps: ExecStep[] = []
    if (otherPoolBehavior === 'remove') {
      for (const pool of info.otherPools.filter(isControlPoolId)) {
        const display = pool.slice(0, 8)
        steps.push({ label: `Remove from pool ${display}…`, status: 'pending' })
      }
    }
    if (disableTou && info.touEnabled) {
      steps.push({ label: 'Disable Time of Use', status: 'pending' })
    }
    steps.push({ label: 'Add to pool', status: 'pending' })
    return steps
  }

  const startExecuting = async () => {
    if (!checkInfo) return
    cancelledRef.current = false

    const trimmedID = systemID.trim()
    const activeFromISO = activeFrom ? new Date(activeFrom).toISOString() : undefined
    const initialSteps = buildSteps(checkInfo)
    setExecSteps(initialSteps)
    setStep('executing')

    const update = (idx: number, patch: Partial<ExecStep>) =>
      setExecSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)))

    let stepIdx = 0
    let removeFailed = false

    // Remove from other control pools
    if (otherPoolBehavior === 'remove') {
      for (const pool of checkInfo.otherPools.filter(isControlPoolId)) {
        const si = stepIdx
        update(si, { status: 'running' })
        const resp = await apiService.systems.remove(pool, trimmedID)
        if (cancelledRef.current) return
        if (resp.success) {
          update(si, { status: 'done' })
        } else {
          update(si, { status: 'error', error: resp.error })
          removeFailed = true
        }
        stepIdx++
      }
    }

    // Disable ToU
    if (disableTou && checkInfo.touEnabled) {
      const si = stepIdx
      update(si, { status: 'running' })
      const resp = await apiService.tou.disable(trimmedID)
      if (cancelledRef.current) return
      update(si, { status: resp.success ? 'done' : 'error', error: resp.success ? undefined : resp.error })
      stepIdx++
    }

    // Add to pool
    const addIdx = stepIdx
    if (removeFailed) {
      update(addIdx, { status: 'skipped' })
    } else {
      update(addIdx, { status: 'running' })
      const resp = await apiService.systems.add(poolID, trimmedID, activeFromISO)
      if (cancelledRef.current) return
      if (resp.success) {
        update(addIdx, { status: 'done' })
        qc.invalidateQueries({ queryKey: ['systems', poolID] })
        qc.invalidateQueries({ queryKey: ['system-owners', trimmedID] })
      } else {
        update(addIdx, { status: 'error', error: resp.error })
      }
    }

    setStep('done')
  }

  // ── Derived state for confirm step ─────────────────────────────────────────

  const willBeSkipped =
    otherPoolBehavior === 'skip' && (checkInfo?.otherPools.length ?? 0) > 0

  const canProceed =
    checkInfo != null &&
    !checkInfo.alreadyInTargetPool &&
    !willBeSkipped &&
    !checkInfo.checkError

  // ── Descriptions ───────────────────────────────────────────────────────────

  const description = {
    form: 'Enter the system ID and activation time.',
    checking: 'Checking system…',
    confirm: 'Review what will happen before proceeding.',
    executing: 'Processing — please wait.',
    done: 'Operation complete.',
  }[step]

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && !isExecuting && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add System to Pool</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">

          {/* ── FORM ── */}
          {step === 'form' && (
            <>
              {needsPhaseLimitsNotice && (
                <div className="flex items-start gap-2 rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    The battery system must have <strong>phase limits configured</strong> when fuse
                    protection is active, or <strong>fuse protection must be disabled</strong>.
                  </span>
                </div>
              )}
              <div className="space-y-2">
                <label htmlFor="systemID" className="text-sm font-medium">System ID</label>
                <input
                  id="systemID"
                  type="text"
                  placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000"
                  value={systemID}
                  onChange={(e) => setSystemID(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-muted-foreground">The UUID of the system to add to this pool</p>
              </div>

              <div className="space-y-2">
                <label htmlFor="activeFrom" className="text-sm font-medium">
                  Active From <span className="text-muted-foreground font-normal">(Optional)</span>
                </label>
                <input
                  id="activeFrom"
                  type="datetime-local"
                  value={activeFrom}
                  onChange={(e) => setActiveFrom(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-muted-foreground">
                  When the system should start participating in this pool (your local time). If not specified, defaults to now.
                </p>
              </div>

              <div className="space-y-2">
                <div className="rounded-md border p-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    System already in another pool
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Info className="h-4 w-4 flex-shrink-0" />
                    A system can only be part of one control pool. No restrictions on forecast pools.
                  </p>
                  {(['skip', 'add', 'remove'] as OtherPoolBehavior[]).map((value) => (
                    <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="otherPoolBehavior"
                        value={value}
                        checked={otherPoolBehavior === value}
                        onChange={() => setOtherPoolBehavior(value)}
                        className="h-4 w-4"
                      />
                      {value === 'add' && (
                        <span>
                          Add to this pool anyway
                          {isControlPool && (
                            <span className="block text-xs text-muted-foreground">
                              Note: When the system is already part of a control pool, adding it to a new control pool will fail.
                            </span>
                          )}
                        </span>
                      )}
                      {value === 'skip' && (
                        <span>
                          Skip
                          <span className="block text-xs text-muted-foreground">
                            Don't add if already in another pool (control or forecast)
                          </span>
                        </span>
                      )}
                      {value === 'remove' && 'Remove from current control pool before adding'}
                    </label>
                  ))}
                </div>
                {isControlPool && (
                  <>
                    <label className={cn('flex items-center gap-2 text-sm', touFallback ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}>
                      <input
                        type="checkbox"
                        checked={disableTou}
                        onChange={(e) => setDisableTou(e.target.checked)}
                        disabled={touFallback}
                        className="h-4 w-4 rounded border-input"
                      />
                      Disable Time of Use when adding
                    </label>
                    {touFallback && (
                      <p className="text-xs text-muted-foreground pl-6">
                        Not available — this pool uses Time of Use as its fallback behavior.
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* ── CHECKING ── */}
          {step === 'checking' && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Checking system…</p>
            </div>
          )}

          {/* ── CONFIRM ── */}
          {step === 'confirm' && checkInfo && (
            <div className="space-y-3">
              {checkInfo.checkError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{checkInfo.checkError}</span>
                </div>
              )}

              {!checkInfo.checkError && checkInfo.alreadyInTargetPool && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>System is already in this pool.</span>
                </div>
              )}

              {!checkInfo.checkError && !checkInfo.alreadyInTargetPool && willBeSkipped && (
                <div className="flex items-start gap-2 rounded-md border p-3 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>System is already in another pool and will be skipped.</span>
                </div>
              )}

              {canProceed && (
                <div className="rounded-md border overflow-hidden">
                  {buildSteps(checkInfo).map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 border-b last:border-0 text-sm">
                      <StatusIcon status="pending" />
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {!isControlPool && canProceed && (
                <p className="text-sm text-muted-foreground">
                  Time of Use:{' '}
                  <span className={checkInfo.touEnabled ? 'text-amber-600' : ''}>
                    {checkInfo.touEnabled ? 'enabled' : 'disabled'}
                  </span>{' '}— will not be changed.
                </p>
              )}

              {!checkInfo.checkError && !checkInfo.alreadyInTargetPool && !willBeSkipped &&
                otherPoolBehavior === 'remove' && checkInfo.otherPools.some(isControlPoolId) && (
                <p className="text-sm text-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  System will be removed from its current control pool before being added here.
                </p>
              )}
            </div>
          )}

          {/* ── EXECUTING / DONE ── */}
          {(step === 'executing' || step === 'done') && (
            <div className="space-y-2">
              {execSteps.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2 rounded-md border p-3 text-sm">
                  <StatusIcon status={s.status} />
                  <span className={s.status === 'error' ? 'text-destructive' : ''}>{s.label}</span>
                  {s.error && <span className="text-destructive text-xs">— {s.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'form' && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={startChecking}>Check System</Button>
            </>
          )}
          {step === 'checking' && (
            <Button variant="outline" onClick={() => { cancelledRef.current = true; setStep('form') }}>
              Cancel
            </Button>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('form')}>Back</Button>
              {canProceed && (
                <Button onClick={() => {
                  if (otherPoolBehavior === 'remove' && checkInfo?.otherPools.some(isControlPoolId)) {
                    setShowDeleteConfirm(true)
                  } else {
                    startExecuting()
                  }
                }}>Add System</Button>
              )}
              {(checkInfo?.alreadyInTargetPool || willBeSkipped) && (
                <Button variant="outline" onClick={handleClose}>Close</Button>
              )}
            </>
          )}
          {step === 'executing' && (
            <Button variant="outline" disabled>Please wait…</Button>
          )}
          {step === 'done' && (
            <Button onClick={handleClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showDeleteConfirm} onOpenChange={(o) => { if (!o) { setShowDeleteConfirm(false); setDeleteConfirmText('') } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Confirm removal</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove these systems from their old control pool?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Type <strong className="text-foreground">DELETE</strong> to confirm.
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText('') }}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteConfirmText !== 'DELETE'}
            onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); startExecuting() }}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
